import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingTopicBySlug } from '@/content/marketing-keywords';

type SeoConfig = {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
};

const siteUrl = 'https://careertree.app';
const defaultDescription =
  'CareerTree.app is AI-powered career planning software that builds personalized learning roadmaps, actionable skill plans, progress tracking, and portfolio evidence.';
const defaultImage = `${siteUrl}/og-image.png`;

const publicSeo: Record<string, SeoConfig> = {
  '/': {
    title: 'CareerTree.app | Turn career goals into proof',
    description: defaultDescription,
    canonical: `${siteUrl}/`,
  },
  '/success-board': {
    title: 'Success Board | CareerTree.app',
    description:
      'See public career wins from CareerTree learners, including progress wins, interviews, certificates, and hired stories.',
    canonical: `${siteUrl}/success-board`,
  },
  '/pricing': {
    title: 'Pricing | CareerTree.app',
    description: 'See CareerTree.app pricing for Free, Pro launch promotion, and planned Career Sprint offerings.',
    canonical: `${siteUrl}/pricing`,
  },
  '/terms': {
    title: 'Terms of Service | CareerTree.app',
    description: 'Read the CareerTree.app terms of service for accounts, subscriptions, acceptable use, and service availability.',
    canonical: `${siteUrl}/policy/terms`,
  },
  '/privacy': {
    title: 'Privacy Policy | CareerTree.app',
    description: 'Read how CareerTree.app handles account data, learning data, billing data, AI features, and privacy requests.',
    canonical: `${siteUrl}/policy/privacy`,
  },
  '/refund': {
    title: 'Refund Policy | CareerTree.app',
    description: 'Read the CareerTree.app refund policy for subscriptions, renewals, billing issues, and cancellation handling.',
    canonical: `${siteUrl}/policy/refund`,
  },
  '/support': {
    title: 'Support and Operations | CareerTree.app',
    description: 'Learn how CareerTree.app handles account help, billing support, public Win issues, incidents, and data requests.',
    canonical: `${siteUrl}/policy/support`,
  },
  '/policy/terms': {
    title: 'Terms of Service | CareerTree.app',
    description: 'Read the CareerTree.app terms of service for accounts, subscriptions, acceptable use, and service availability.',
    canonical: `${siteUrl}/policy/terms`,
  },
  '/policy/privacy': {
    title: 'Privacy Policy | CareerTree.app',
    description: 'Read how CareerTree.app handles account data, learning data, billing data, AI features, and privacy requests.',
    canonical: `${siteUrl}/policy/privacy`,
  },
  '/policy/refund': {
    title: 'Refund Policy | CareerTree.app',
    description: 'Read the CareerTree.app refund policy for subscriptions, renewals, billing issues, and cancellation handling.',
    canonical: `${siteUrl}/policy/refund`,
  },
  '/policy/support': {
    title: 'Support and Operations | CareerTree.app',
    description: 'Learn how CareerTree.app handles account help, billing support, public Win issues, incidents, and data requests.',
    canonical: `${siteUrl}/policy/support`,
  },
  '/policy/ai-notice': {
    title: 'AI Notice | CareerTree.app',
    description: 'Read how CareerTree.app uses AI assistance for learning trees, quizzes, mini actions, and proof review.',
    canonical: `${siteUrl}/policy/ai-notice`,
  },
};

const noindexPatterns = [
  /^\/app(?:\/|$)/,
  /^\/auth(?:\/|$)/,
  /^\/career\/portfolio(?:\/|$)/,
  /^\/complete-profile(?:\/|$)/,
  /^\/dashboard(?:\/|$)/,
  /^\/knowledge(?:\/|$)/,
  /^\/login(?:\/|$)/,
  /^\/profile(?:\/|$)/,
  /^\/publication(?:\/|$)/,
  /^\/signup(?:\/|$)/,
  /^\/skills(?:\/|$)/,
  /^\/timeline(?:\/|$)/,
];

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function getSeoConfig(pathname: string): SeoConfig {
  if (publicSeo[pathname]) {
    return publicSeo[pathname];
  }

  const resourceMatch = pathname.match(/^\/resources\/([^/]+)$/);
  if (resourceMatch) {
    const topic = getMarketingTopicBySlug(resourceMatch[1]);

    if (topic) {
      return {
        title: `${topic.title} | CareerTree.app`,
        description: topic.summary,
        canonical: `${siteUrl}/resources/${topic.slug}`,
      };
    }
  }

  if (noindexPatterns.some((pattern) => pattern.test(pathname))) {
    return {
      title: 'CareerTree.app',
      description: defaultDescription,
      canonical: `${siteUrl}${pathname}`,
      robots: 'noindex, nofollow',
    };
  }

  return {
    title: 'CareerTree.app | Turn career goals into proof',
    description: defaultDescription,
    canonical: `${siteUrl}${pathname}`,
  };
}

export function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoConfig(location.pathname);
    const robots = seo.robots || 'index, follow, max-image-preview:large';

    document.title = seo.title;

    upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: robots });
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: seo.canonical });

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: seo.canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: defaultImage });

    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: defaultImage });
  }, [location.pathname]);

  return null;
}
